import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Calendar } from 'lucide-react';
import { Combobox } from './Combobox';
import { DateNavigation } from './DateNavigation';

interface ScheduleControlsProps {
  organizations: Array<{ id: string; slug?: string }>;
  providers: Array<{ id: string; name: string }>;
  facilities: Array<{ id: string; name: string }>;
  selectedOrgId: string;
  selectedProviderId: string;
  selectedFacilityId: string;
  selectedDate: string;
  onOrgChange: (orgId: string) => void;
  onProviderChange: (providerId: string) => void;
  onFacilityChange: (facilityId: string) => void;
  onDateChange: (date: string) => void;
  providersLoading: boolean;
  facilitiesLoading: boolean;
  scheduleLoading: boolean;
}

export function ScheduleControls({
  organizations,
  providers,
  facilities,
  selectedOrgId,
  selectedProviderId,
  selectedFacilityId,
  selectedDate,
  onOrgChange,
  onProviderChange,
  onFacilityChange,
  onDateChange,
  providersLoading,
  facilitiesLoading,
  scheduleLoading,
}: ScheduleControlsProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Calendar className="h-5 w-5" />
          Schedule Controls
        </CardTitle>
        <CardDescription>
          Select organization, provider, facility, and date to view the schedule
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {/* Organization - Full width */}
          <div>
            <div className="text-sm font-medium mb-2 block">Organization</div>
            <Select value={selectedOrgId} onValueChange={onOrgChange}>
              <SelectTrigger>
                <SelectValue placeholder="Select Organization" />
              </SelectTrigger>
              <SelectContent>
                {organizations?.map((org) => (
                  <SelectItem key={org.id} value={org.id}>
                    {org.slug || 'Unnamed Organization'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Provider and Facility - 2 columns */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <div className="text-sm font-medium mb-2 block">Provider</div>
              <Combobox
                options={providers || []}
                value={selectedProviderId}
                onValueChange={onProviderChange}
                placeholder="Select Provider"
                searchPlaceholder="Search providers..."
                disabled={!selectedOrgId || providersLoading}
                emptyMessage={
                  providersLoading ? 'Loading providers...' : 'No providers found for search'
                }
              />
            </div>

            <div>
              <div className="text-sm font-medium mb-2 block">Facility (Optional)</div>
              <Combobox
                options={facilities || []}
                value={selectedFacilityId}
                onValueChange={onFacilityChange}
                placeholder="All Facilities"
                searchPlaceholder="Search facilities..."
                disabled={!selectedOrgId || facilitiesLoading}
                emptyMessage={
                  facilitiesLoading ? 'Loading facilities...' : 'No facilities found for search'
                }
              />
            </div>
          </div>

          {/* Date Navigation */}
          <div>
            <div className="text-sm font-medium mb-2 block">Date</div>
            <DateNavigation
              selectedDate={selectedDate}
              onDateChange={onDateChange}
              disabled={scheduleLoading}
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
